<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { usePedidosStore } from "../../../services/pedidos";
import { userUserStore } from "../../../stores/user";
import { getFirstDayOfMonth } from "../../../utils/helper";
import { getCurrentDate } from "../../../utils/fecha";
import { storeToRefs } from "pinia";
import ExcelJS from "exceljs";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  Timestamp,
  where
} from "firebase/firestore";

const pedidosStore = usePedidosStore();
const { filtros } = storeToRefs(pedidosStore);
const userStore = userUserStore();

const db = getFirestore();

const cargando = ref(false);

watch(
  () => [filtros.value.fechaInicio, filtros.value.fechaFin],
  async ([inicio, fin]) => {
    if (!inicio || !fin) return;

    cargando.value = true;
    await cargarDescuentos();
    cargando.value = false;
  }
);
const estadisticasDescuentos = ref<any[]>([]);
const parseFecha = (fecha: string) => {
  const [dia, mes, anio] = fecha.split("/");
  return new Date(Number(anio), Number(mes) - 1, Number(dia));
};
const cargarDescuentos = async () => {
  const inicio = parseFecha(filtros.value.fechaInicio);
  const fin = parseFecha(filtros.value.fechaFin);

  fin.setHours(23, 59, 59, 999);

  const q = query(
    collection(db, `empresas/${userStore.empresa?.id}/estadisticas-descuentos`),
    where("fecha", ">=", Timestamp.fromDate(inicio)),
    where("fecha", "<=", Timestamp.fromDate(fin))
  );

  const snap = await getDocs(q);

  const resultados: any[] = [];

  snap.forEach(doc => {
    resultados.push(doc.data());
  });

  estadisticasDescuentos.value = resultados;
};

const descuentosAgrupados = computed(() => {
  const mapa: Record<
    string,
    {
      tipoDescuento: string;
      cantidad: number;
      porcentaje: number;
      descuento: number;
    }
  > = {};

  for (const item of estadisticasDescuentos.value) {
    const key = item.tipoDescuento;

    if (!mapa[key]) {
      mapa[key] = {
        tipoDescuento: key,
        cantidad: 0,
        porcentaje: item.porcentajeDescuento,
        descuento: 0
      };
    }

    mapa[key].cantidad += item.cantidad;
    mapa[key].descuento += item.descuento;
  }

  return Object.values(mapa).sort((a, b) => b.descuento - a.descuento);
});

const chartData = computed(() => {
  const result: Record<string, number> = {};

  for (const doc of estadisticasDescuentos.value) {
    if (!result[doc.rubro]) {
      result[doc.rubro] = 0;
    }

    result[doc.rubro] += doc.monto;
  }

  return result;
});

onBeforeMount(async () => {
  filtros.value.fechaInicio = getFirstDayOfMonth();
  filtros.value.fechaFin = getCurrentDate();
  cargando.value = true;
  await cargarDescuentos();
  cargando.value = false;
});

const handleExport = async () => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet("Hoja 1");
  const encabezados = [
    "Tipo de descuento",
    "$ del descuento",
    "Cantidad",
    "Total descontado"
  ];
  worksheet.addRow(encabezados);

  for (const estadistica of descuentosAgrupados.value) {
    const datos = [
      estadistica.tipoDescuento,
      estadistica.porcentaje,
      estadistica.cantidad.toFixed(2),
      estadistica.descuento.toFixed(2)
    ];
    worksheet.addRow(datos);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Estadísticas descuentos - ${filtros.value.fechaInicio}- ${filtros.value.fechaFin}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};
</script>

<template>
  <Preview class="mt-4 box">
    <Preview.Panel>
      <div class="px-5 min-w-0 my-6">
        <h2 class="my-3 text-base ml-1">Estadísticas de descuentos</h2>
        <div
          class="overflow-y-visible min-w-0 -mx-5 px-5 grid grid-cols-3 gap-6"
        >
          <div class="col-span-3 max-h-[300px] overflow-y-auto">
            <Table bordered hover striped class="min-w-[700px]">
              <Table.Thead class="bg-[#E09C2D] text-white">
                <Table.Tr>
                  <Table.Th class="whitespace-nowrap">
                    Tipo de descuento</Table.Th
                  >
                  <Table.Th class="whitespace-nowrap">
                    % del descuento</Table.Th
                  >
                  <Table.Th class="whitespace-nowrap"> Cantidad</Table.Th>
                  <Table.Th class="whitespace-nowrap">
                    Total descontado
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <template
                  v-for="estadistica in descuentosAgrupados"
                  :key="estadistica.tipoDescuento"
                >
                  <Table.Tr
                    class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                  >
                    <Table.Td>
                      {{ estadistica.tipoDescuento.replace("_", " ") }}
                    </Table.Td>
                    <Table.Td>{{ estadistica.cantidad }} </Table.Td>
                    <Table.Td>{{ estadistica.cantidad.toFixed(2) }}</Table.Td>
                    <Table.Td>{{ estadistica.descuento.toFixed(2) }}</Table.Td>
                  </Table.Tr>
                </template>
                <Table.Tr
                  class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                  v-if="estadisticasDescuentos.length === 0"
                >
                  <Table.Td
                    colspan="30"
                    class="text-center py-8 text-slate-500"
                  >
                    No hay estadísticas para mostrar.
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </div>
        </div>
        <div
          @click="handleExport"
          class="flex relative group mt-6 cursor-pointer ml-1"
        >
          <Lucide icon="Download" class="w-4 h-4 mr-2" />
          <p>Exportar datos a Excel</p>
        </div>
      </div>
    </Preview.Panel>
  </Preview>
</template>
